import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { MashoService } from 'src/app/services/masho-manager/masho.service';
import { MashoManagerModalService } from 'src/app/services/masho-manager-modal/masho-manager-modal.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-masho-manager-add-edit',
  templateUrl: './masho-manager-add-edit.component.html',
  styleUrls: ['./masho-manager-add-edit.component.scss']
})
export class MashoManagerAddEditComponent extends DestructorComponent implements OnInit {

  @Output() mashoAdded = new EventEmitter<void>();
  @Output() mashoUpdated = new EventEmitter<void>();

  mashoForm: FormGroup;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedMasho: any = null;
  private preSelectedVehicle: any = null; // Vehicle pre-selected from external source
  private isVehicleDisabled: boolean = false; // Flag to disable vehicle selection
  private isVehicleAlreadyExists: boolean = false;

  // Vehicle search properties
  vehicleSearchTerm: string = '';
  vehicleSearchResults: any[] = [];
  selectedVehicle: any = null;
  showVehicleDropdown: boolean = false;
  isVehicleSearching: boolean = false;
  private vehicleSearchSubject = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private _mashoService: MashoService,
    private _mashoManagerModalService: MashoManagerModalService,
    private _vehicleService: VehicleService
  ) {
    super();
    this.initializeForm();
    this.setupVehicleSearchDebounce();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.mashoForm = this.fb.group({
      vehicle_id: ['', [Validators.required]],
      vehicle_name: ['', [Validators.required]],
      masho_date: ['', [Validators.required]],
      valid_until_date: ['', [Validators.required]],
      admin_comment: ['']
    });
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_masho_manager_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceMasho = this._mashoManagerModalService.getSelectedMasho();
        const preSelectedVehicle = this._mashoManagerModalService.getPreSelectedVehicle();
        console.log({ preSelectedVehicle });
        if (preSelectedVehicle)
          this.getMashoByVehicleId(preSelectedVehicle._id);


        if (serviceMasho) {
          this.isEditMode = true;
          this.selectedMasho = serviceMasho;
          this.isVehicleDisabled = true; // Disable vehicle selection in edit mode
          this.populateForm(this.selectedMasho);
        } else if (preSelectedVehicle) {
          this.isEditMode = false;
          this.selectedMasho = null;
          this.preSelectedVehicle = preSelectedVehicle;
          this.isVehicleDisabled = true; // Disable vehicle selection when pre-selected
          this.resetForm();
          this.populateVehicleFromPreSelected(preSelectedVehicle);
        } else {
          this.isEditMode = false;
          this.selectedMasho = null;
          this.preSelectedVehicle = null;
          this.isVehicleDisabled = false; // Enable vehicle selection for normal add mode
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._mashoManagerModalService.clearSelectedMasho();
        this._mashoManagerModalService.clearPreSelectedVehicle();
        this.resetForm();
      });
    }
  }

  private populateForm(masho: any): void {
    if (masho.vehicle) {
      this.selectedVehicle = masho.vehicle;
      this.vehicleSearchTerm = this.getVehicleDisplayName(masho.vehicle);
    }

    this.mashoForm.patchValue({
      vehicle_id: masho.vehicle_id || masho.vehicle?._id || masho.vehicle?.id || '',
      vehicle_name: this.vehicleSearchTerm,
      masho_date: masho.masho_date || '',
      valid_until_date: masho.valid_until_date || '',
      admin_comment: masho.admin_comment || ''
    });
  }

  private populateVehicleFromPreSelected(vehicle: any): void {
    this.selectedVehicle = vehicle;
    this.vehicleSearchTerm = this.getVehicleDisplayName(vehicle);

    this.mashoForm.patchValue({
      vehicle_id: vehicle._id || vehicle.id,
      vehicle_name: this.vehicleSearchTerm
    });
  }

  private resetForm(): void {
    this.mashoForm.reset();
    this.selectedVehicle = null;
    this.vehicleSearchTerm = '';
    this.vehicleSearchResults = [];
    this.showVehicleDropdown = false;
    this.isVehicleSearching = false;
    this.isLoading = false;
    this.preSelectedVehicle = null;
    this.isVehicleDisabled = false;
  }

  onSubmit(): void {
    if (this.mashoForm.valid && !this.isLoading) {
      this.isLoading = true;

      let formData: any = { ...this.mashoForm.value };

      if (this.isEditMode && this.selectedMasho) {
        // In edit mode, don't send vehicle_name and vehicle_id
        delete formData.vehicle_name;
        delete formData.vehicle_id;
        this.updateMasho(formData);
      } else {
        // In add mode, include vehicle_id
        formData.vehicle_id = this.selectedVehicle?._id || this.selectedVehicle?.id || this.mashoForm.value.vehicle_id;
        this.addMasho(formData);
      }
    } else {
      this.markFormGroupTouched(this.mashoForm);
    }
  }

  getVehicleImageUrl(vehicle: any): string {
    if (vehicle.pictures && vehicle.pictures.length > 0) {
      return vehicle.pictures[0];
    }
    return 'assets/media/svg/files/blank-image.svg'; // Default placeholder image
  }

  private getMashoByVehicleId(vehicle_id: string) {

    this.subscriptions.push(
      this._mashoService.GetData('1', '', { vehicle_id, paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.isVehicleAlreadyExists = response.data.length > 0;
            if (response.data.length) {
              let masho = response.data[0];
              this.mashoForm.patchValue({
                vehicle_id: masho.vehicle_id || masho.vehicle?._id || masho.vehicle?.id || '',
                vehicle_name: masho.vehicle_name,
                masho_date: masho.masho_date || '',
                valid_until_date: masho.valid_until_date || '',
                admin_comment: masho.admin_comment || ''
              });
              this.isEditMode = true;
              this.selectedMasho = masho;
              this.isVehicleDisabled = true;
            }
          }
        },
        error: (error: any) => {
          console.error('Error fetching masho data:', error);
        }
      })
    );

  }

  private addMasho(data: any): void {
    this.subscriptions.push(
      this._mashoService.AddData(data).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Masho added successfully',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.mashoAdded.emit();
            });
          } else {
            this.handleError(response.message || 'Failed to add masho');
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          this.handleError(error.error?.message || 'An error occurred while adding masho');
        }
      })
    );
  }

  private updateMasho(data: any): void {
    const mashoId = this.selectedMasho._id || this.selectedMasho.id;
    this.subscriptions.push(
      this._mashoService.UpdateData(mashoId, data).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Masho updated successfully',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.closeModal();
              this.mashoUpdated.emit();
            });
          } else {
            this.handleError(response.message || 'Failed to update masho');
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          this.handleError(error.error?.message || 'An error occurred while updating masho');
        }
      })
    );
  }

  private handleError(message: string): void {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonText: 'OK'
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_masho_manager_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Form control getters
  get vehicle_id() { return this.mashoForm.get('vehicle_id'); }
  get vehicle_name() { return this.mashoForm.get('vehicle_name'); }
  get masho_date() { return this.mashoForm.get('masho_date'); }
  get valid_until_date() { return this.mashoForm.get('valid_until_date'); }
  get admin_comment() { return this.mashoForm.get('admin_comment'); }

  // Utility getters
  get isVehicleSelectionDisabled(): boolean {
    return this.isVehicleDisabled;
  }

  // Vehicle search methods
  private setupVehicleSearchDebounce(): void {
    this.vehicleSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      this.searchVehicles(searchTerm);
    });
  }

  onVehicleSearchInput(event: Event): void {
    // Don't allow search if vehicle selection is disabled
    if (this.isVehicleDisabled) {
      return;
    }

    const target = event.target as HTMLInputElement;
    this.vehicleSearchTerm = target.value;
    this.selectedVehicle = null;

    // Update the form control with the typed value
    this.mashoForm.patchValue({
      vehicle_name: this.vehicleSearchTerm,
      vehicle_id: ''
    });

    if (this.vehicleSearchTerm.length >= 2) {
      this.vehicleSearchSubject.next(this.vehicleSearchTerm);
      this.showVehicleDropdown = true;
    } else {
      this.showVehicleDropdown = false;
      this.vehicleSearchResults = [];
    }
  }

  onVehicleInputFocus(): void {
    if (!this.isVehicleDisabled && this.vehicleSearchResults.length > 0) {
      this.showVehicleDropdown = true;
    }
  }

  onVehicleInputBlur(): void {
    if (!this.isVehicleDisabled) {
      setTimeout(() => {
        this.showVehicleDropdown = false;
      }, 200);
    }
  }

  private searchVehicles(searchTerm: string): void {
    if (!searchTerm || searchTerm.length < 2) {
      this.vehicleSearchResults = [];
      this.showVehicleDropdown = false;
      return;
    }

    this.isVehicleSearching = true;
    this.subscriptions.push(
      this._vehicleService.GetData('1', searchTerm, { paginate: false, search: searchTerm }).subscribe({
        next: (response: any) => {
          this.isVehicleSearching = false;
          if (response.status && response.data) {
            // Handle both paginated and non-paginated responses
            this.vehicleSearchResults = response.data.data || response.data || [];
            this.showVehicleDropdown = this.vehicleSearchResults.length > 0;
          } else {
            this.vehicleSearchResults = [];
            this.showVehicleDropdown = false;
          }
        },
        error: (error: any) => {
          this.isVehicleSearching = false;
          console.error('Error searching vehicles:', error);
          this.vehicleSearchResults = [];
          this.showVehicleDropdown = false;
        }
      })
    );
  }

  selectVehicle(vehicle: any): void {
    this.selectedVehicle = vehicle;
    this.vehicleSearchTerm = this.getVehicleDisplayName(vehicle);

    this.mashoForm.patchValue({
      vehicle_id: vehicle._id || vehicle.id,
      vehicle_name: this.vehicleSearchTerm
    });

    this.showVehicleDropdown = false;
  }

  private getVehicleDisplayName(vehicle: any): string {
    const parts = [];
    if (vehicle.car_no) parts.push(vehicle.car_no);
    // if (vehicle.vin_no) parts.push(vehicle.vin_no);
    if (vehicle.company_name) parts.push(vehicle.company_name);
    if (vehicle.name) parts.push(vehicle.name);
    if (vehicle.year) parts.push(`(${vehicle.year})`);
    return parts.join(' ') || 'Unknown Vehicle';
  }
}
